"use client";

import {
  Activity,
  CreditCard,
  Layers,
  type LucideIcon,
  RefreshCw,
  ShieldCheck,
  Webhook,
} from "lucide-react";
import { motion } from "motion/react";
import { useState } from "react";
import {
  FadeInUp,
  StaggerContainer,
  StaggerItem,
} from "@/components/ui/motion";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  ObservabilityIllustration,
  PricingIllustration,
  ProductionGradeIllustration,
  RetriesIllustration,
  VisualBuilderIllustration,
  WebhooksIllustration,
} from "./FeatureIllustrations";

interface Feature {
  icon: LucideIcon;
  title: string;
  description: string;
  illustration: React.ComponentType<{ isHovered: boolean }>;
  gridArea: string;
  size: "hero" | "medium" | "small";
}

const features: Feature[] = [
  {
    icon: Layers,
    title: "Visual Orchestration",
    description:
      "Low-code builder with high-code flexibility. Design complex logic without the infrastructure overhead.",
    illustration: VisualBuilderIllustration,
    gridArea: "hero",
    size: "hero",
  },
  {
    icon: Activity,
    title: "Deep Observability",
    description:
      "Real-time execution traces with millisecond precision. Audit every state change and data transformation.",
    illustration: ObservabilityIllustration,
    gridArea: "obs",
    size: "medium",
  },
  {
    icon: Webhook,
    title: "Enterprise Webhooks",
    description:
      "High-throughput webhook handling with signature verification and idempotent routing logic.",
    illustration: WebhooksIllustration,
    gridArea: "webhook",
    size: "medium",
  },
  {
    icon: RefreshCw,
    title: "Resilient Retries",
    description:
      "Automatic recovery with configurable exponential backoff. Ensure zero-loss data delivery.",
    illustration: RetriesIllustration,
    gridArea: "retry",
    size: "small",
  },
  {
    icon: CreditCard,
    title: "Predictable Pricing",
    description:
      "Usage-based billing that scales with your growth. No hidden fees or enterprise tax.",
    illustration: PricingIllustration,
    gridArea: "price",
    size: "small",
  },
  {
    icon: ShieldCheck,
    title: "Production-Ready",
    description:
      "Built for reliability with environment isolation, granular logs, and 24/7 monitoring.",
    illustration: ProductionGradeIllustration,
    gridArea: "prod",
    size: "small",
  },
];

// Card size configurations - only for styling, not layout
const sizeConfig = {
  hero: {
    iconSize: "w-10 h-10",
    iconInner: "w-5 h-5",
    titleSize: "text-base",
    descSize: "text-sm",
    padding: "p-5",
    illustrationHeight: "min-h-[140px]",
  },
  medium: {
    iconSize: "w-9 h-9",
    iconInner: "w-4 h-4",
    titleSize: "text-sm",
    descSize: "text-xs",
    padding: "p-4",
    illustrationHeight: "min-h-[120px]",
  },
  small: {
    iconSize: "w-8 h-8",
    iconInner: "w-4 h-4",
    titleSize: "text-sm",
    descSize: "text-xs",
    padding: "p-4",
    illustrationHeight: "min-h-[110px]",
  },
};

// Grid area classes for explicit placement
const gridAreaClasses: Record<string, string> = {
  hero: "md:[grid-area:hero]",
  obs: "md:[grid-area:obs]",
  webhook: "md:[grid-area:webhook]",
  retry: "md:[grid-area:retry]",
  price: "md:[grid-area:price]",
  prod: "md:[grid-area:prod]",
};

function FeatureCard({ feature }: { feature: Feature }) {
  const [isHovered, setIsHovered] = useState(false);
  const isMobile = useIsMobile();
  const config = sizeConfig[feature.size];

  return (
    <StaggerItem className={gridAreaClasses[feature.gridArea]}>
      <motion.div
        onHoverStart={() => !isMobile && setIsHovered(true)}
        onHoverEnd={() => !isMobile && setIsHovered(false)}
        whileHover={isMobile ? {} : { y: -3 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className={`group relative h-full ${config.padding} rounded-2xl bg-card border border-border shadow-sm hover:shadow-lg hover:border-primary/40 transition-all duration-300 overflow-hidden flex flex-col`}
      >
        {/* Gradient overlay on hover */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-br from-primary/8 via-transparent to-transparent pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: isHovered ? 1 : 0 }}
          transition={{ duration: 0.45 }}
        />

        {/* Content */}
        <div className="relative flex flex-col h-full">
          {/* Header: Icon + Text */}
          <div className="flex-shrink-0">
            {/* Icon */}
            <motion.div
              className={`${config.iconSize} rounded-xl bg-primary/10 flex items-center justify-center mb-2 border border-primary/20`}
              animate={isHovered ? { scale: 1.05 } : { scale: 1 }}
              transition={{ duration: 0.25 }}
            >
              <feature.icon className={`${config.iconInner} text-primary`} />
            </motion.div>

            {/* Title & Description */}
            <h3
              className={`${config.titleSize} font-semibold text-foreground mb-1 tracking-tight`}
            >
              {feature.title}
            </h3>
            <p
              className={`${config.descSize} text-muted-foreground/90 leading-relaxed line-clamp-2`}
            >
              {feature.description}
            </p>
          </div>

          {/* Illustration - fixed height based on size */}
          <div
            className={`${config.illustrationHeight} mt-3 flex-1 flex items-center justify-center`}
          >
            <feature.illustration isHovered={isHovered} />
          </div>
        </div>
      </motion.div>
    </StaggerItem>
  );
}

export function Features() {
  return (
    <section className="py-20 lg:py-28 section-elevated relative">
      {/* Decorative grid pattern */}
      <div className="absolute inset-0 pattern-grid opacity-20 pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        <FadeInUp className="max-w-2xl mx-auto text-center mb-14">
          <h2 className="text-foreground mb-4 tracking-tight">
            Built for <span className="gradient-text">production</span>{" "}
            workflows
          </h2>
          <p className="text-lg text-muted-foreground/90">
            Everything you need to automate with confidence.
          </p>
        </FadeInUp>

        {/* Bento Grid with explicit grid-template-areas - balanced 2-row layout */}
        <StaggerContainer className="max-w-5xl mx-auto grid grid-cols-1 gap-4 md:grid-cols-8 md:grid-rows-[260px_220px_220px] md:[grid-template-areas:'hero_hero_hero_hero_obs_obs_webhook_webhook'_'hero_hero_hero_hero_retry_retry_webhook_webhook'_'price_price_price_prod_prod_prod_prod_prod']">
          {features.map((feature, index) => (
            <FeatureCard key={index} feature={feature} />
          ))}
        </StaggerContainer>
      </div>
    </section>
  );
}
